import React, { useState, useEffect } from 'react';
import { X, Plus, Save, GripVertical, Trash2, AlertCircle, ArrowUp, ArrowDown } from 'lucide-react';
import { useCreateListView, useUpdateListView, type ListView, type ListViewColumn, type SortConfig } from '../../../services/queries/listViewQueries';
import { FilterBuilder } from './FilterBuilder';
import { ListViewPreview } from './ListViewPreview';
import { useToast } from '../../../contexts/ToastContext';

interface ListViewBuilderProps {
  view?: ListView;
  objectName: string;
  objectLabel: string;
  allFields: any[];
  onClose: () => void;
}

export const ListViewBuilder: React.FC<ListViewBuilderProps> = ({
  view: initialView,
  objectName,
  objectLabel,
  allFields,
  onClose
}) => {
  const [viewData, setViewData] = useState<Partial<ListView>>({
    name: initialView?.name || '',
    objectName,
    description: initialView?.description || '',
    columns: initialView?.columns || [],
    filters: initialView?.filters || [],
    sort: initialView?.sort || [],
    isDefault: initialView?.isDefault || false,
    isShared: initialView?.isShared || true
  });

  const [showPreview, setShowPreview] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  const createViewMutation = useCreateListView();
  const updateViewMutation = useUpdateListView();
  const { success, error } = useToast();

  // Get fields not yet in columns
  const usedFieldIds = new Set(viewData.columns?.map(c => c.fieldId) || []);
  const availableFields = allFields.filter(field => !usedFieldIds.has(field.id));

  useEffect(() => {
    if (initialView) {
      setViewData({
        name: initialView.name,
        objectName: initialView.objectName,
        description: initialView.description,
        columns: initialView.columns,
        filters: initialView.filters,
        sort: initialView.sort,
        isDefault: initialView.isDefault,
        isShared: initialView.isShared
      });
    }
  }, [initialView]);

  const handleAddColumn = (fieldId: string) => {
    const field = allFields.find(f => f.id === fieldId);
    if (!field) return;

    const newColumn: ListViewColumn = {
      fieldId: field.id,
      fieldLabel: field.fieldLabel,
      fieldType: field.fieldType,
      sortable: true
    };

    setViewData(prev => ({
      ...prev,
      columns: [...(prev.columns || []), newColumn]
    }));
    setHasChanges(true);
  };

  const handleRemoveColumn = (fieldId: string) => {
    setViewData(prev => ({
      ...prev,
      columns: prev.columns?.filter(c => c.fieldId !== fieldId) || []
    }));
    setHasChanges(true);
  };

  const handleReorderColumn = (fromIndex: number, toIndex: number) => {
    const columns = [...(viewData.columns || [])];
    const [movedColumn] = columns.splice(fromIndex, 1);
    columns.splice(toIndex, 0, movedColumn);

    setViewData(prev => ({ ...prev, columns }));
    setHasChanges(true);
  };

  const handleAddSort = (fieldId: string) => {
    const column = viewData.columns?.find(c => c.fieldId === fieldId);
    if (!column) return;

    const newSort: SortConfig = {
      fieldId: column.fieldId,
      fieldLabel: column.fieldLabel,
      direction: 'asc'
    };

    setViewData(prev => ({
      ...prev,
      sort: [...(prev.sort || []), newSort]
    }));
    setHasChanges(true);
  };

  const handleToggleSortDirection = (fieldId: string) => {
    setViewData(prev => ({
      ...prev,
      sort: prev.sort?.map(s =>
        s.fieldId === fieldId
          ? { ...s, direction: s.direction === 'asc' ? 'desc' : 'asc' }
          : s
      )
    }));
    setHasChanges(true);
  };

  const handleRemoveSort = (fieldId: string) => {
    setViewData(prev => ({
      ...prev,
      sort: prev.sort?.filter(s => s.fieldId !== fieldId) || []
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!viewData.name) {
      error('View name required', 'Please enter a view name');
      return;
    }

    if (!viewData.columns || viewData.columns.length === 0) {
      error('No columns configured', 'Please add at least one column');
      return;
    }

    try {
      if (initialView) {
        await updateViewMutation.mutateAsync({
          viewId: initialView.id,
          updates: viewData
        });
      } else {
        await createViewMutation.mutateAsync(viewData as Omit<ListView, 'id' | 'createdBy' | 'createdDate' | 'lastModified'>);
      }
      success('List view saved successfully!');
      setHasChanges(false);
      onClose();
    } catch (err: any) {
      error('Failed to save list view', err.message || 'Please try again');
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full h-full max-w-[98vw] max-h-[98vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {initialView ? 'Edit' : 'Create'} List View
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {objectLabel} • Configure columns, filters, and sorting
            </p>
          </div>

          <div className="flex items-center gap-3">
            {hasChanges && (
              <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400">
                <AlertCircle className="w-4 h-4" />
                Unsaved changes
              </div>
            )}

            <button
              onClick={() => setShowPreview(!showPreview)}
              className={`px-3 py-2 rounded-lg transition-colors ${
                showPreview
                  ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Preview
            </button>

            <button
              onClick={handleSave}
              disabled={!hasChanges || createViewMutation.isPending || updateViewMutation.isPending}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {createViewMutation.isPending || updateViewMutation.isPending ? 'Saving...' : 'Save View'}
            </button>

            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-900 dark:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Available Fields */}
          <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Available Fields ({availableFields.length})
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              <div className="space-y-2">
                {availableFields.map(field => (
                  <button
                    key={field.id}
                    onClick={() => handleAddColumn(field.id)}
                    className="w-full text-left px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 hover:border-purple-300 dark:hover:border-purple-600 transition-colors"
                  >
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {field.fieldLabel}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {field.fieldType}
                    </div>
                  </button>
                ))}

                {availableFields.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                    All fields added
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Center Panel - Configuration */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Basic Info */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Basic Information
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      View Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={viewData.name}
                      onChange={(e) => {
                        setViewData(prev => ({ ...prev, name: e.target.value }));
                        setHasChanges(true);
                      }}
                      placeholder="e.g., Active Clients"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={viewData.description || ''}
                      onChange={(e) => {
                        setViewData(prev => ({ ...prev, description: e.target.value }));
                        setHasChanges(true);
                      }}
                      placeholder="Describe this list view..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={viewData.isShared}
                        onChange={(e) => {
                          setViewData(prev => ({ ...prev, isShared: e.target.checked }));
                          setHasChanges(true);
                        }}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Share with all users
                      </span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={viewData.isDefault}
                        onChange={(e) => {
                          setViewData(prev => ({ ...prev, isDefault: e.target.checked }));
                          setHasChanges(true);
                        }}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Set as default view
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Selected Columns */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Selected Columns ({viewData.columns?.length || 0})
                </h3>

                {viewData.columns && viewData.columns.length > 0 ? (
                  <div className="space-y-2">
                    {viewData.columns.map((column, index) => (
                      <div
                        key={column.fieldId}
                        className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                      >
                        <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />

                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {column.fieldLabel}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {column.fieldType}
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          {index > 0 && (
                            <button
                              onClick={() => handleReorderColumn(index, index - 1)}
                              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                              title="Move up"
                            >
                              <ArrowUp className="w-4 h-4 text-gray-500" />
                            </button>
                          )}
                          {viewData.columns && index < viewData.columns.length - 1 && (
                            <button
                              onClick={() => handleReorderColumn(index, index + 1)}
                              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                              title="Move down"
                            >
                              <ArrowDown className="w-4 h-4 text-gray-500" />
                            </button>
                          )}
                          <button
                            onClick={() => handleRemoveColumn(column.fieldId)}
                            className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No columns added yet
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Click fields on the left to add them
                    </p>
                  </div>
                )}
              </div>

              {/* Filters */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <FilterBuilder
                  filters={viewData.filters || []}
                  availableFields={allFields}
                  onChange={(filters) => {
                    setViewData(prev => ({ ...prev, filters }));
                    setHasChanges(true);
                  }}
                />
              </div>

              {/* Sort Configuration */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Sort Order ({viewData.sort?.length || 0})
                  </h3>
                  {viewData.columns && viewData.columns.length > 0 && (
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddSort(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Add Sort Field...</option>
                      {viewData.columns
                        .filter(c => !viewData.sort?.some(s => s.fieldId === c.fieldId))
                        .map(column => (
                          <option key={column.fieldId} value={column.fieldId}>
                            {column.fieldLabel}
                          </option>
                        ))}
                    </select>
                  )}
                </div>

                {viewData.sort && viewData.sort.length > 0 ? (
                  <div className="space-y-2">
                    {viewData.sort.map((sort, index) => (
                      <div
                        key={sort.fieldId}
                        className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                      >
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 w-8">
                          {index + 1}.
                        </span>

                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {sort.fieldLabel}
                          </div>
                        </div>

                        <button
                          onClick={() => handleToggleSortDirection(sort.fieldId)}
                          className="px-3 py-1 text-sm bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded hover:bg-purple-200 dark:hover:bg-purple-900/30 transition-colors flex items-center gap-1"
                        >
                          {sort.direction === 'asc' ? (
                            <>
                              <ArrowUp className="w-3 h-3" />
                              Ascending
                            </>
                          ) : (
                            <>
                              <ArrowDown className="w-3 h-3" />
                              Descending
                            </>
                          )}
                        </button>

                        <button
                          onClick={() => handleRemoveSort(sort.fieldId)}
                          className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No sort fields configured
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Preview */}
          {showPreview && (
            <ListViewPreview
              view={viewData}
              objectLabel={objectLabel}
            />
          )}
        </div>
      </div>
    </div>
  );
};
